import { Button, Modal, TextAreaField, TextField, WriteError } from '@shared/ui';
import { useState } from 'react';

import { isReleaseVersion } from '../api/release-create';
import {
    releaseEditDraftFrom,
    releaseEditInputFrom,
    type ReleaseEditDraft,
} from '../api/release-edit';
import { useUpdateRelease } from '../hooks/use-release-components';

import { explainReleaseWriteFailure } from './release-error-copy';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * Edit a release's version and flags. Name and description are not editable and
 * are shown nowhere in this form: the API refuses a body that carries them.
 *
 * Mounted only while open (the page renders it conditionally), so the draft
 * starts from the release as last read each time and a stale draft never
 * outlives a refetch. Its logic lives in `api/release-edit.ts`.
 */
export function EditReleaseDialog({
    release,
    onClose,
}: {
    release: ReleaseComponent;
    onClose: () => void;
}) {
    const [draft, setDraft] = useState<ReleaseEditDraft>(() => releaseEditDraftFrom(release));
    const update = useUpdateRelease(release.id);
    const input = releaseEditInputFrom(draft, release);
    const failure = update.error ? explainReleaseWriteFailure(update.error) : null;

    const set = (field: keyof ReleaseEditDraft, value: string) => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const submit = () => {
        if (input === null) return;
        update.mutate(input, { onSuccess: onClose });
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!update.isPending}
            title="Edit release"
            description="Change the version label or the flags in this release. Removing a flag changes no flag and withdraws no override."
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={input === null || update.isPending}>
                        {update.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                <TextField
                    label="Release version"
                    value={draft.releaseVersion}
                    onChange={(value) => {
                        set('releaseVersion', value);
                    }}
                    mono
                    error={
                        draft.releaseVersion.trim().length > 0 &&
                        !isReleaseVersion(draft.releaseVersion)
                            ? 'Use 1-32 characters of letters, digits, `.`, `_` or `-`.'
                            : undefined
                    }
                />
                <TextAreaField
                    label="Flags"
                    value={draft.flags}
                    onChange={(value) => {
                        set('flags', value);
                    }}
                    rows={6}
                    hint="One flag key per line. Only flags you add are checked: a flag archived since it was added stays in the release."
                />
                <TextAreaField
                    label="Reason (optional)"
                    value={draft.reason}
                    onChange={(value) => {
                        set('reason', value);
                    }}
                    rows={2}
                    hint="Recorded on the audit entry."
                />
                {failure ? (
                    <div
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                    >
                        <p className="font-semibold">{failure.title}</p>
                        <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                        {failure.detail !== undefined && (
                            <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                        )}
                    </div>
                ) : (
                    update.error && <WriteError error={update.error} />
                )}
            </div>
        </Modal>
    );
}
